// src/components/Auth/AuthRoutes.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { auth } from '../../services/firebase';
import { applyActionCode } from 'firebase/auth';

import Login from './Login';
import AccountCreation from './AccountCreation';
import EmailVerificationSuccess from './EmailVerificationSuccess';
import './Auth.css';
import banner from '../../assets-shop/artifact-banner.png';

// Email verification handler component
export const EmailVerificationHandler = () => {
  const [status, setStatus] = useState('verifying');
  const location = useLocation();
  
  useEffect(() => {
    const handleVerification = async () => {
      try {
        // Get verification code from URL
        const queryParams = new URLSearchParams(location.search);
        const actionCode = queryParams.get('oobCode');
        
        if (!actionCode) {
          setStatus('error');
          return;
        }
        
        // Apply the verification code
        await applyActionCode(auth, actionCode);
        
        // Update user's verification status
        if (auth.currentUser) {
          await auth.currentUser.reload();
        }
        
        setStatus('success');
      } catch (error) {
        console.error('Error verifying email:', error);
        setStatus('error');
      }
    };
    
    handleVerification();
  }, [location]);
  
  // Show appropriate message based on status
  switch (status) {
    case 'verifying':
      return (
        <div className="auth-verification-container">
          <div className="auth-verification-card">
            <div className="auth-logo">
              <img src={banner} alt="Artifact" className="auth-banner" />
            </div>
            <h2>Verifying Your Email</h2>
            <p>Please wait while we verify your email address...</p>
          </div>
        </div>
      );
    case 'success':
      return <EmailVerificationSuccess />;
    case 'error':
      return (
        <div className="auth-verification-container">
          <div className="auth-verification-card">
            <div className="auth-logo">
              <img src={banner} alt="Artifact" className="auth-banner" />
            </div>
            <h2>Verification Failed</h2>
            <p>We couldn't verify your email. The link may have expired or already been used.</p>
            <Link to="/login" className="auth-button primary-button">
              Back to Login
            </Link>
            <div className="auth-support-links">
              <p>
                Need help? <a href="mailto:support@artifact-app.com" className="text-link">Contact Support</a>
              </p>
            </div>
          </div>
        </div>
      );
    default:
      return null;
  }
};

// Protected route component - redirects to login if not authenticated
export const ProtectedRoute = ({ children }) => {
  const user = auth.currentUser;
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (!user.emailVerified) {
    return <Navigate to="/verify-email" />;
  }
  
  return children;
};

// Auth routes component
const AuthRoutesComponent = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<AccountCreation />} />
      <Route path="/verify-email" element={<EmailVerificationHandler />} />
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );
};

// Export the AuthRoutes component as default
export default AuthRoutesComponent;